import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { useEffect, useState } from "react"

interface ProgressBarCardProps {
  label: string
  actual: number
  goal: number
  completion: number
  color?: string
  className?: string
  animate?: boolean
  delay?: number
  useSecondaryBackground?: boolean
}

export function ProgressBarCard({
  label,
  actual,
  goal,
  completion,
  color,
  className,
  animate = false,
  delay = 0,
  useSecondaryBackground = false
}: ProgressBarCardProps) {
  // Cap completion at 100% for visual display
  const cappedCompletion = Math.min(completion, 100)
  
  const [animatedCompletion, setAnimatedCompletion] = useState(animate ? 0 : cappedCompletion)
  
  // Use theme primary color as default if no color provided
  const progressColor = color || "var(--primary)"
  
  // Create background color with proper opacity
  const backgroundColor = useSecondaryBackground
    ? "var(--secondary)" // Use theme secondary color (already an oklch color)
    : color 
      ? `${color}20` // For API colors like #f4511e, this becomes #f4511e20
      : "oklch(from var(--primary) l c h / 0.2)" // For theme primary, use proper CSS syntax

  useEffect(() => {
    if (!animate) return

    const timeout = setTimeout(() => {
      const duration = 1500 // 1.5 seconds
      const startTime = Date.now()
      const startValue = 0

      const animateValue = () => {
        const elapsed = Date.now() - startTime
        const progress = Math.min(elapsed / duration, 1)
        
        // Linear animation (constant speed)
        const currentValue = startValue + (cappedCompletion - startValue) * progress
        
        setAnimatedCompletion(currentValue)
        
        if (progress < 1) {
          requestAnimationFrame(animateValue)
        }
      }

      requestAnimationFrame(animateValue)
    }, delay)

    return () => clearTimeout(timeout)
  }, [cappedCompletion, animate, delay])
  
  return (
    <Card className={cn("border-0 shadow-none py-2", className)}>
      <CardContent className="p-0">
        <div className="flex items-center justify-between mb-3">
          {/* Label in bold on top left */}
          <div className="font-bold text-sm">
            {label}
          </div>
          
          {/* Result string on top right */}
          <div className="text-sm text-muted-foreground">
            ({actual}h/{goal}h) {completion}%
          </div>
        </div>
        
        {/* Progress bar */}
        <div 
          className="relative h-2 w-full overflow-hidden rounded-full"
          style={{ 
            backgroundColor: backgroundColor
          }}
        >
          <div
            className="h-full w-full flex-1 transition-all duration-300"
            style={{ 
              transform: `translateX(-${100 - (animatedCompletion || 0)}%)`,
              backgroundColor: progressColor
            }}
          />
        </div>
      </CardContent>
    </Card>
  )
}